'use client';
import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Send, MessageCircle, CornerDownRight, Flag } from 'lucide-react';
import ReportModal from '@/components/ReportModal';
import { createNotification } from '@/lib/notifications';

interface Comment {
  id: string;
  body: string;
  created_at: string;
  parent_id: string | null;
  author: {
    id: string;
    display_name: string | null;
    username: string | null;
    avatar_url: string | null;
    role: string;
  };
  replies?: Comment[];
}

interface Props {
  postId: string;
  isLoggedIn: boolean;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getInitial(name: string | null) {
  return name ? name.charAt(0).toUpperCase() : '?';
}

function CommentItem({ comment, onReply, isLoggedIn }: { comment: Comment; onReply: (parentId: string, body: string) => Promise<void>; isLoggedIn: boolean }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    await onReply(comment.id, replyText);
    setReplyText('');
    setReplyOpen(false);
    setSubmitting(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-3 group">
        {comment.author?.avatar_url ? (
          <img src={comment.author.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-charcoal-600 to-charcoal-800 flex items-center justify-center text-xs font-semibold text-white shrink-0">
            {getInitial(comment.author?.display_name)}
          </div>
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-charcoal-900">{comment.author?.display_name || comment.author?.username}</span>
            <span className="text-xs text-charcoal-400">{formatDate(comment.created_at)}</span>
            {isLoggedIn && (
              <button
                onClick={() => setReportOpen(true)}
                className="ml-auto opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 text-charcoal-300 hover:text-red-500 transition-all"
                title="Report comment">
                <Flag size={11} />
              </button>
            )}
          </div>
          <p className="text-sm text-charcoal-700 leading-relaxed">{comment.body}</p>
          {isLoggedIn && (
            <button onClick={() => setReplyOpen(!replyOpen)}
              className="flex items-center gap-1 text-xs text-charcoal-400 hover:text-charcoal-700 mt-1.5 transition-colors">
              <CornerDownRight size={11} />
              Reply
            </button>
          )}
          {replyOpen && (
            <div className="mt-2 flex gap-2">
              <input value={replyText} onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write a reply…"
                className="flex-1 px-3 py-2 bg-cream-100 border border-cream-300 rounded-lg text-sm text-charcoal-900 placeholder:text-charcoal-400 focus:outline-none focus:ring-2 focus:ring-gold-400/40 focus:border-gold-400 transition-all" />
              <button onClick={handleReply} disabled={!replyText.trim() || submitting}
                className="px-3 py-2 bg-charcoal-900 text-white rounded-lg text-sm hover:bg-charcoal-800 disabled:opacity-40 transition-colors">
                {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Nested replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="ml-11 space-y-3 border-l-2 border-cream-300 pl-4">
          {comment.replies.map(reply => (
            <CommentItem key={reply.id} comment={reply} onReply={onReply} isLoggedIn={isLoggedIn} />
          ))}
        </div>
      )}
      {reportOpen && (
        <ReportModal commentId={comment.id} onClose={() => setReportOpen(false)} />
      )}
    </div>
  );
}

export default function CommentsSection({ postId, isLoggedIn }: Props) {
  const { user, profile } = useAuth();
  const supabase = createClient();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = async () => {
    const { data } = await supabase
      .from('comments')
      .select(`id, body, created_at, parent_id, author:profiles!comments_author_id_fkey(id, display_name, username, avatar_url, role)`)
      .eq('post_id', postId)
      .eq('is_hidden', false)
      .order('created_at', { ascending: true });

    if (data) {
      const topLevel = (data as any[]).filter(c => !c.parent_id);
      const replies = (data as any[]).filter(c => c.parent_id);
      const nested = topLevel.map(c => ({
        ...c,
        replies: replies.filter(r => r.parent_id === c.id),
      }));
      setComments(nested);
    }
    setLoading(false);
  };

  useEffect(() => { fetchComments(); }, [postId]);

  const handleSubmit = async () => {
    if (!user || !newComment.trim()) return;
    setSubmitting(true);
    const { data: insertedComment, error } = await supabase.from('comments').insert({
      post_id: postId,
      author_id: user.id,
      body: newComment.trim(),
      parent_id: null,
    }).select('id').single();
    if (!error) {
      setNewComment('');
      // Notify post author
      const { data: postData } = await supabase
        .from('posts')
        .select('author_id')
        .eq('id', postId)
        .single();
      if (postData?.author_id) {
        await createNotification({
          recipientId: postData.author_id,
          actorId: user.id,
          type: 'comment',
          postId,
          commentId: insertedComment?.id,
        });
      }
      await fetchComments();
    }
    setSubmitting(false);
  };

  const handleReply = async (parentId: string, body: string) => {
    if (!user) return;
    const { data: insertedReply } = await supabase.from('comments').insert({
      post_id: postId,
      author_id: user.id,
      body: body.trim(),
      parent_id: parentId,
    }).select('id').single();
    // Notify parent comment author
    const { data: parentComment } = await supabase
      .from('comments')
      .select('author_id')
      .eq('id', parentId)
      .single();
    if (parentComment?.author_id) {
      await createNotification({
        recipientId: parentComment.author_id,
        actorId: user.id,
        type: 'reply',
        postId,
        commentId: insertedReply?.id,
      });
    }
    await fetchComments();
  };

  return (
    <div className="px-6 py-5">
      {/* Comment input */}
      {isLoggedIn ? (
        <div className="flex gap-3 mb-5">
          {profile?.avatar_url ? (
            <img src={profile.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gold-300 to-gold-500 flex items-center justify-center text-xs font-semibold text-white shrink-0">
              {getInitial(profile?.display_name)}
            </div>
          )}
          <div className="flex-1 flex gap-2">
            <input value={newComment} onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSubmit()}
              placeholder="Share your perspective…"
              className="flex-1 px-3 py-2.5 bg-cream-100 border border-cream-300 rounded-lg text-sm text-charcoal-900 placeholder:text-charcoal-400 focus:outline-none focus:ring-2 focus:ring-gold-400/40 focus:border-gold-400 transition-all" />
            <button onClick={handleSubmit} disabled={!newComment.trim() || submitting}
              className="px-4 py-2.5 bg-charcoal-900 text-white rounded-lg text-sm font-medium hover:bg-charcoal-800 disabled:opacity-40 active:scale-95 transition-all">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-charcoal-500 mb-4 text-center">
          <a href="/sign-up-login-screen" className="text-gold-500 hover:text-gold-600">Sign in</a> to join the conversation
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 size={20} className="animate-spin text-charcoal-400" />
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-6">
          <MessageCircle size={24} className="mx-auto text-charcoal-300 mb-2" />
          <p className="text-sm text-charcoal-400">No comments yet. Start the conversation.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {comments.map(comment => (
            <CommentItem key={comment.id} comment={comment} onReply={handleReply} isLoggedIn={isLoggedIn} />
          ))}
        </div>
      )}
    </div>
  );
}
